
// Robust local-plane estimation for the point-cloud-adaptive Cirkel/
// Rektangel capture (see tools/circleTool.js / tools/rectangleTool.js and
// tools/livePlaneEstimator.js). No DOM/THREE dependency - plain {x,y,z}
// points, Level 1 (no-browser) unit testable - see localPlaneFit.test.js.
//
// Deliberately reuses volume/referencePlane.js's own eigen-solver/tangent-
// frame primitives (jacobiEigenSymmetric3, tangentFrame) rather than a
// second PCA implementation - see that file's own header for why Jacobi
// rotation is the numerically stable choice for a near-planar (possibly
// noisy) point set. Unlike fitBoundaryPlane() there (which fits a
// length-weighted POLYGON BOUNDARY), this fits a plain, equal-weighted
// point cloud (raw octree ROI samples), so it has its own much smaller
// centroid/covariance step.
//
// ── Principle (task spec) ───────────────────────────────────────────────
//   preliminary figure -> observe nearby point cloud -> robust plane fit
//   -> regenerate the exact figure IN that plane
// This module is only the middle step. It never touches figure vertices
// directly - callers project their own anchor/reference points onto the
// returned plane (projectOntoPlane below) and rebuild the exact figure
// from THAT (sampleCirclePolygon / fitRectangleFromThreePoints +
// buildRectangleCorners) - see those tools' own capture code. So the point
// cloud only ever ORIENTS the figure; it can never deform it directly.
//
// ── Robustness ────────────────────────────────────────────────────────
// One short trim-and-refit pass (fit once, drop points further than
// TRIM_SIGMA standard deviations from that first plane, refit on what's
// left) - a single-iteration trimmed least squares, cheap enough to run
// on every mousemove-throttled step, and enough to keep a handful of
// outliers (a stray high point, a bit of vegetation at the ROI's edge)
// from dragging the whole plane off - without the cost/complexity of a
// full iterative RANSAC.
//
// ── Stability under mousemove ────────────────────────────────────────────
// Two distinct failure modes are guarded against explicitly:
//   - insufficient/unreliable support (too few points, or residuals too
//     large relative to the query radius to trust as "one local surface")
//     -> falls back to a caller-supplied deterministic plane (never
//     "no plane"; every caller gets a complete, usable frame every time).
//   - PCA's own sign ambiguity (the smallest-eigenvalue eigenvector is
//     only defined up to a sign) -> `previousNormal`, when given, orients
//     the new normal to agree with the LAST accepted fit instead of
//     flipping the whole figure to its mirror orientation from one
//     mousemove sample to the next.

import {jacobiEigenSymmetric3, tangentFrame} from "./volume/referencePlane.js";

function sub(a, b){ return {x: a.x - b.x, y: a.y - b.y, z: a.z - b.z}; }
function add(a, b){ return {x: a.x + b.x, y: a.y + b.y, z: a.z + b.z}; }
function dot(a, b){ return a.x * b.x + a.y * b.y + a.z * b.z; }
function scale(a, s){ return {x: a.x * s, y: a.y * s, z: a.z * s}; }
function normalize(a){
	const len = Math.hypot(a.x, a.y, a.z);
	return len > 1e-12 ? scale(a, 1 / len) : {x: 0, y: 0, z: 0};
}

// World point -> its orthogonal projection onto `plane` ({origin, normal}).
// The one place the point cloud's estimated plane actually touches a
// figure's own geometry - always a projection (moves a point only along
// the plane's normal), never a free-form deformation.
export function projectOntoPlane(point, plane){
	const d = dot(sub(point, plane.origin), plane.normal);
	return sub(point, scale(plane.normal, d));
}

const MIN_POINTS = 8;             // below this, PCA is too noise-sensitive to trust - fall back
const TRIM_SIGMA = 2.5;           // outlier-trim threshold, in residual standard deviations
const MAX_RESIDUAL_RATIO = 0.35;  // rms residual vs. query radius - above this, not a reliable single local surface

function fitOnce(points){
	const n = points.length;
	let origin = {x: 0, y: 0, z: 0};
	for(const p of points) origin = add(origin, p);
	origin = scale(origin, 1 / n);

	const M = [0, 0, 0, 0, 0, 0]; // xx, xy, xz, yy, yz, zz
	for(const p of points){
		const a = sub(p, origin);
		M[0] += a.x * a.x; M[1] += a.x * a.y; M[2] += a.x * a.z;
		M[3] += a.y * a.y; M[4] += a.y * a.z; M[5] += a.z * a.z;
	}
	for(let i = 0; i < 6; i++) M[i] /= n;

	const {eigenvalues, eigenvectors} = jacobiEigenSymmetric3(M);
	let minIdx = 0;
	for(let i = 1; i < 3; i++) if(eigenvalues[i] < eigenvalues[minIdx]) minIdx = i;
	const normal = normalize({x: eigenvectors[minIdx][0], y: eigenvectors[minIdx][1], z: eigenvectors[minIdx][2]});

	const residuals = points.map((p) => dot(sub(p, origin), normal));
	const rms = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / n);

	return {origin, normal, residuals, rms};
}

// points: [{x,y,z}, ...] - raw ROI query result, world space, equal weight.
// options:
//   previousNormal   - {x,y,z}|null, the last ACCEPTED fit's normal (sign
//                       continuity - see header comment)
//   fallbackOrigin/
//   fallbackNormal   - {x,y,z}, the stable default plane used whenever
//                       point-cloud support is insufficient
//   queryRadius      - number|null, the ROI query's own radius (used as the
//                       residual/extent scale for the planarity gate; falls
//                       back to the point set's own spread when omitted)
//
// Returns {ok, origin, normal, uAxis, vAxis, pointCount, rms?} - ALWAYS a
// complete, usable frame (ok:false just means "this is the fallback plane,
// not one estimated from data" - callers never need a separate no-data
// branch).
export function fitPlaneFromPoints(points, {previousNormal = null, fallbackOrigin, fallbackNormal, queryRadius = null} = {}){
	const fallback = () => {
		const normal = normalize(fallbackNormal);
		const {uAxis, vAxis} = tangentFrame(normal);
		return {ok: false, origin: fallbackOrigin, normal, uAxis, vAxis, pointCount: points.length};
	};

	if(points.length < MIN_POINTS) return fallback();

	// short trim-and-refit loop (task: "en kort iterativ refinement"), capped
	// at TRIM_ITERATIONS passes - each pass drops points far from the
	// CURRENT best plane and refits on what's left, so a coordinated cluster
	// of outliers that survives one pass (its own residual inflates that
	// pass's own rms/threshold) still gets caught once the plane has
	// tightened up on a later pass. Stops early once a pass removes nothing
	// (converged) or would drop below MIN_POINTS (nothing more to trim).
	const TRIM_ITERATIONS = 3;
	let fit = fitOnce(points);
	let currentPoints = points;
	for(let iter = 0; iter < TRIM_ITERATIONS; iter++){
		const threshold = Math.max(fit.rms * TRIM_SIGMA, 1e-6);
		const trimmed = currentPoints.filter((p, i) => Math.abs(fit.residuals[i]) <= threshold);
		if(trimmed.length === currentPoints.length || trimmed.length < MIN_POINTS) break;
		currentPoints = trimmed;
		fit = fitOnce(currentPoints);
	}

	const extentScale = queryRadius || Math.max(1e-6, ...points.map((p) => Math.hypot(p.x - fit.origin.x, p.y - fit.origin.y, p.z - fit.origin.z)));
	if(fit.rms > MAX_RESIDUAL_RATIO * extentScale) return fallback(); // too noisy/non-planar to trust

	let normal = fit.normal;
	if(previousNormal){
		if(dot(normal, previousNormal) < 0) normal = scale(normal, -1);
	}else if(dot(normal, {x: 0, y: 0, z: 1}) < 0){
		// first-ever fit of this capture session: default to "pointing up",
		// matching this codebase's own established convention (fitBoundaryPlane,
		// arcFromSagitta's determineArcPlane)
		normal = scale(normal, -1);
	}

	const {uAxis, vAxis} = tangentFrame(normal);
	return {ok: true, origin: fit.origin, normal, uAxis, vAxis, pointCount: points.length, rms: fit.rms};
}
